import type { ReactNode } from "react";
import type { User } from "@common/types/user";
import { usePresenceViewers } from "@hooks/presence/usePresenceViewers";
import {
    useOrganizationPresenceUsers,
    usePresenceUsers,
} from "./usePresenceUsers";
import {
    useTaskViewersTarget,
    useTasksOrganizationId,
} from "./useTaskViewersTarget";

type RenderViewers = (viewers: User[]) => ReactNode;

interface ResolvedViewersProps {
    userIds: number[];
    children: RenderViewers;
}

const WorkspaceViewerUsers = ({ userIds, children }: ResolvedViewersProps) =>
    children(usePresenceUsers(userIds));

const OrganizationViewerUsers = ({ userIds, children }: ResolvedViewersProps) =>
    children(useOrganizationPresenceUsers(userIds));

/**
 * Renders `children` with `userIds` resolved against the workspace's members,
 * or the organization's staff on organization-wide pages.
 */
export const ResolvedViewerUsers = ({
    userIds,
    children,
}: ResolvedViewersProps) => {
    const ResolvedViewers =
        useTasksOrganizationId() === null
            ? WorkspaceViewerUsers
            : OrganizationViewerUsers;
    return <ResolvedViewers userIds={userIds}>{children}</ResolvedViewers>;
};

/**
 * Renders `children` with the other users viewing a task right now, resolved
 * against the workspace's members, or the organization's staff on
 * organization-wide pages. Viewers whose member isn't known are left out.
 */
export const TaskViewerUsers = ({
    taskId,
    workspaceId,
    children,
}: {
    taskId: string;
    workspaceId: string;
    children: RenderViewers;
}) => {
    const userIds = usePresenceViewers(
        useTaskViewersTarget({ taskId, workspaceId }),
    );
    return (
        <ResolvedViewerUsers userIds={userIds}>{children}</ResolvedViewerUsers>
    );
};
